import { Loading } from '@p2p/components';
import { PaymentTemplate } from '@p2p/entities/templates';
import { MatchingPaymentErrorBoundary } from '@p2p/pages/MatchingPayment/MatchingPaymentErrorBoundary';
import { useGetMerchantOrderQuery, usePageSettings } from '@p2p/services';
import { IN_DISPUTE, isTerminalOrderStatus } from '@p2p/shared/constants';
import { routes } from '@p2p/shared/routing';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';

import { useProofPaymentNavigation } from './ProofPayment.hooks';

// P2P-2880: статус заявки опрашивается раз в 2 секунды.
const POLLING_INTERVAL = 2000;

/**
 * P2P-2880: платёжная страница proof_page — пополнение с обязательной загрузкой
 * PDF-чека. Визуально как msp_page, но заявка PayGo: без выбора суммы, реквизит
 * выдан сразу, чек и диспут уходят трейдеру — владельцу реквизита.
 *
 * web_analytics и receipt из настроек страницы здесь намеренно не используются.
 */
export const ProofPayment = () => {
  const { id = '' } = useParams<{ id: string }>();
  const [pollingInterval, setPollingInterval] = useState(POLLING_INTERVAL);
  const {
    isLoading,
    isError,
    data: order,
    isFetching,
  } = useGetMerchantOrderQuery(id, { skip: !id, pollingInterval });
  const {
    data: pageSettings,
    isLoading: isLoadingPageSettings,
    isFetching: isFetchingPageSettings,
  } = usePageSettings({ order });

  useProofPaymentNavigation(order, pageSettings, isFetching || isFetchingPageSettings);

  useEffect(() => {
    // P2P-2776: в конечном статусе заявка больше не меняется, в диспуте плательщику
    // уже показан итоговый экран — опрашивать check_order дальше незачем (открытая
    // вкладка иначе опрашивает его часами и ловит блокировку на Cloudflare).
    if (isTerminalOrderStatus(order?.status) || order?.status === IN_DISPUTE) {
      setPollingInterval(0);
    }
  }, [order?.status]);

  // Заявки нет (404) или id битый — не держим плательщика на вечном лоадере.
  if (!order && isError) {
    return <Navigate to={routes.notFoundError.template} replace />;
  }

  if (isLoading || !order) {
    return <Loading />;
  }

  // Заявка не с proof_page (ссылку открыли вручную или тема сменилась) — отдаём её
  // общей точке входа, она сама откроет нужную страницу. Проверка до настроек
  // страницы: для чужих тем они не запрашиваются, и без неё висел бы лоадер.
  if (order.payment_form !== 'proof_page') {
    return <Navigate to={routes.transfer.path({ id: order.id })} replace />;
  }

  if (isLoadingPageSettings || !pageSettings) {
    return <Loading />;
  }

  return (
    <MatchingPaymentErrorBoundary>
      <PaymentTemplate>
        <Outlet />
      </PaymentTemplate>
    </MatchingPaymentErrorBoundary>
  );
};
